import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectCrossClientSelectedTab, selectCrossClientTabCounts, setCrossClientSelectedTab } from '../../../../store/crossClient/crossClientResults'
import {
  CROSS_CLIENT_INCOMPLETE_TAB,
  CrossClientQueryResultMatches,
  CrossClientResultTabs as RESULT_TABS,
  type CrossClientResultTab,
} from '../../../../models/CrossClient'
import { ReactComponent as MatchIcon } from '../../../../assets/images/cross-client-match.svg'
import { ReactComponent as PartialMatchIcon } from '../../../../assets/images/cross-client-partial-match.svg'
import { ReactComponent as NoMatchIcon } from '../../../../assets/images/cross-client-no-match.svg'
import './CrossClientResultTabs.scss'

const isResultTab = (value: string): value is CrossClientResultTab => RESULT_TABS.includes(value as CrossClientResultTab)

interface TabConfig {
  tab: CrossClientResultTab
  label: string
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>
}

const TAB_CONFIG: TabConfig[] = [
  { tab: CrossClientQueryResultMatches.MATCH, label: 'Match', Icon: MatchIcon },
  { tab: CrossClientQueryResultMatches.PARTIAL, label: 'Potential', Icon: PartialMatchIcon },
  { tab: CROSS_CLIENT_INCOMPLETE_TAB, label: 'Incomplete' },
  { tab: CrossClientQueryResultMatches.NO_MATCH, label: 'No match', Icon: NoMatchIcon },
]

export const CrossClientResultTabs = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const counts = useAppSelector(selectCrossClientTabCounts)

  const countByTab: Record<CrossClientResultTab, number> = {
    [CrossClientQueryResultMatches.MATCH]: counts.full,
    [CrossClientQueryResultMatches.PARTIAL]: counts.partial,
    [CROSS_CLIENT_INCOMPLETE_TAB]: counts.incomplete,
    [CrossClientQueryResultMatches.NO_MATCH]: counts.noMatch,
  }

  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true
    const tabParam = searchParams.get('tab')
    if (tabParam && isResultTab(tabParam) && tabParam !== selectedTab) {
      dispatch(setCrossClientSelectedTab(tabParam))
    }
  }, [dispatch, searchParams, selectedTab])

  const handleSelect = (tab: CrossClientResultTab) => {
    if (tab === selectedTab) {
      return
    }
    dispatch(setCrossClientSelectedTab(tab))
    setSearchParams(
      (prev) => {
        prev.set('tab', tab)
        return prev
      },
      { replace: true },
    )
  }

  return (
    <div className='d-flex align-items-center gap-1 cross-client-result-tabs'>
      {TAB_CONFIG.map(({ tab, label, Icon }) => (
        <button
          key={tab}
          type='button'
          aria-pressed={tab === selectedTab}
          className={classNames('d-flex align-items-center gap-1 cross-client-result-tabs__tab', {
            'cross-client-result-tabs__tab--active': tab === selectedTab,
          })}
          onClick={() => handleSelect(tab)}
        >
          {Icon ? <Icon width={12} height={12} /> : <span className='cross-client-result-tabs__incomplete-icon' aria-hidden='true' />}
          <span>
            {label} {countByTab[tab].toLocaleString()}
          </span>
        </button>
      ))}
    </div>
  )
}
